import { cancelDomAnimation, startDomDrift } from "../../dom_backend/dom_animations.js";
import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { UIBackgroundFitHeight } from "../../common_ui_elements/ui_background_fit_height.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIGradientOverlay } from "../../common_ui_elements/ui_gradient_overlay.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { GraphicsSettings } from "../../../graphics/graphics_quality.js";
import { Game } from "../../../game.js";

export class AdventureBackground
{
  private readonly ctx: CanvasRenderingContext2D;
  private readonly clouds: { el: UIImage; speed: number; w: number }[] = [];
  private cloud_wrap_w = 0;
  private clouds_last_now = 0;
  private cloud_paint_shift = 0;

  private readonly GROUND_W = 1536;
  private readonly GROUND_H = 793;
  private readonly GROUND_BOTTOM_OVERHANG = 60;

  private readonly CLOUD_DESIGN_W = 436;
  private readonly CLOUD_DESIGN_H = 280;
  private readonly CLOUD_SIZE_MULTIPLIER = 1.75;

  private readonly CLOUDS: { x_t: number, y_t: number, scale: number }[] = [
    { x_t: 0.023, y_t: 0.121, scale: 0.37 },
    { x_t: 0.299, y_t: 0.161, scale: 0.43 },
    { x_t: 0.592, y_t: 0.200, scale: 0.37 },
    { x_t: 0.894, y_t: 0.235, scale: 0.43 },
    { x_t: 0.023, y_t: 0.548, scale: 0.38 },
    { x_t: 0.342, y_t: 0.437, scale: 0.57 },
    { x_t: 0.703, y_t: 0.461, scale: 0.39 },
    { x_t: 0.027, y_t: 0.784, scale: 0.59 },
    { x_t: 0.677, y_t: 0.823, scale: 0.90 },
  ];

  constructor( ctx: CanvasRenderingContext2D )
  {
    this.ctx = ctx;
  }

  buildPinned( screen: UIElement, screen_w: number, viewport_h: number ): void
  {
    const sky = new UIGradientOverlay( this.ctx, 0, 0, screen_w, viewport_h, "#0067F3", "#8FF0FD" );
    screen.addChild( sky );

    const max_cloud_w = LayoutScaler.widthFromDesign( this.CLOUD_DESIGN_W ) * this.CLOUD_SIZE_MULTIPLIER;
    const max_cloud_h = LayoutScaler.heightFromDesign( this.CLOUD_DESIGN_H ) * this.CLOUD_SIZE_MULTIPLIER;
    const layout_scale = LayoutScaler.getScale();

    this.cloud_wrap_w = screen_w;
    this.clouds.length = 0;
    this.clouds_last_now = 0;

    for ( const cloud_def of this.CLOUDS )
    {
      const cloud_w = max_cloud_w * cloud_def.scale;
      const cloud_h = max_cloud_h * cloud_def.scale;

      const x = cloud_def.x_t * ( screen_w - cloud_w );
      const y = cloud_def.y_t * viewport_h - cloud_h / 2;

      const cloud = new UIImage( this.ctx, x, y, cloud_w, cloud_h, ImgID.UI_MenuCloud );
      cloud.setClickable( false );
      screen.addChild( cloud );

      const speed_scale = Math.min( 1, cloud_def.scale );
      const speed = ( 10 + ( 1 - speed_scale ) * 55 + Math.random() * 18 ) * layout_scale;
      this.clouds.push( { el: cloud, speed, w: cloud_w } );
    }
  }

  private cloud_anims: Animation[] = [];

  private startCloudDomDrift(): void
  {
    for ( const cloud of this.clouds )
    {
      const pos = cloud.el.pixelPosImportant();
      const anim = startDomDrift( cloud.el.getDomNode(), pos.x, pos.y, cloud.w, this.cloud_wrap_w, cloud.speed );
      if ( anim )
        this.cloud_anims.push( anim );
    }
  }

  public cancelCloudDomDrift(): void
  {
    for ( const anim of this.cloud_anims )
      cancelDomAnimation( anim );
    this.cloud_anims = [];
  }

  updateClouds( now: number ): boolean
  {
    if ( this.clouds.length === 0 ) return false;

    if ( !GraphicsSettings.profile().sky_animation )
    {
      this.clouds_last_now = now;
      if ( this.cloud_anims.length > 0 )
        this.cancelCloudDomDrift();
      return false;
    }

    if ( this.clouds[0].el.isDomMode() )
    {
      if ( this.cloud_anims.length === 0 )
        this.startCloudDomDrift();
      return false;
    }

    const dt = this.clouds_last_now > 0
      ? Math.min( 0.05, ( now - this.clouds_last_now ) / 1000 )
      : 0;
    this.clouds_last_now = now;

    if ( dt === 0 ) return false;

    let max_move = 0;
    for ( const cloud of this.clouds )
    {
      const pos = cloud.el.pixelPosImportant();
      const movement = cloud.speed * dt;
      let x = pos.x + movement;

      if ( x > this.cloud_wrap_w )
        x = -cloud.w;

      cloud.el.setPixelPos( new Vector2( x, pos.y ), true );
      max_move = Math.max( max_move, movement );
    }

    this.cloud_paint_shift += max_move;
    if ( this.cloud_paint_shift < 1 / Game.getDpr() )
      return false;

    this.cloud_paint_shift = 0;
    return true;
  }

  buildGround( content: UIElement, screen_w: number, content_h: number ): void
  {
    const ground_w = LayoutScaler.widthFromDesign( this.GROUND_W );
    const ground_h = LayoutScaler.heightFromDesign( this.GROUND_H );

    const ground_x = screen_w / 2 - ground_w / 2;
    const ground_y = content_h - ground_h + LayoutScaler.yFromDesign( this.GROUND_BOTTOM_OVERHANG );

    content.addChild( new UIBackgroundFitHeight( this.ctx, ground_x, ground_y, ground_w, ground_h, ImgID.UI_AdventureBg ) );
  }
}
